import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { resolveGameCover } from "../utils/gameBanner";

const hueOf = (value = "") => {
  let hash = 0;
  String(value).split("").forEach((char) => {
    hash = (hash * 31 + char.charCodeAt(0)) % 360;
  });
  return 248 + (hash % 36);
};

const providerOf = (game) =>
  String(game?.provider_game || "")
    .replace(/\s*[·•]\s*RTP\s*\d+.*$/i, "")
    .replace(/pragmatic play.*/i, "Pragmatic Play")
    .trim();

const SlotShowcase = ({ games = [], onOpen, title, moreLabel, moreTo, playLabel = "Oyna" }) => {
  const items = useMemo(() => (games || []).filter((game) => game?.game_id).slice(0, 10), [games]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [broken, setBroken] = useState({});
  const drag = useRef({ x: 0, down: false, moved: false });

  useEffect(() => {
    if (active >= items.length) setActive(0);
  }, [items.length, active]);

  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActive((value) => (value + 1) % items.length);
    }, 4800);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (!items.length) return null;

  const move = (dir) => setActive((value) => (value + dir + items.length) % items.length);

  const onPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    drag.current = { x: event.clientX, down: true, moved: false };
  };

  const onPointerMove = (event) => {
    if (!drag.current.down) return;
    const dx = event.clientX - drag.current.x;
    if (Math.abs(dx) < 46) return;
    drag.current.moved = true;
    drag.current.down = false;
    move(dx < 0 ? 1 : -1);
  };

  const endDrag = () => {
    drag.current.down = false;
  };

  const openIfCenter = (game, offset) => {
    if (drag.current.moved) {
      drag.current.moved = false;
      return;
    }
    if (offset === 0) onOpen(game);
    else setActive(items.findIndex((item) => item.game_id === game.game_id));
  };

  return (
    <section
      className="slot-show"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="slot-show-head">
        <h2>{title}</h2>
        {moreTo ? (
          <Link className="slot-show-more" to={moreTo}>
            {moreLabel}
          </Link>
        ) : null}
      </div>
      <div className="slot-show-stage">
        <button type="button" className="slot-show-nav" onClick={() => move(-1)} aria-label="Sola kaydır">
          <ChevronLeftIcon />
        </button>
        <div
          className="slot-show-track"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
        >
          {items.map((game, index) => {
            let offset = index - active;
            const n = items.length;
            if (offset > n / 2) offset -= n;
            if (offset < -n / 2) offset += n;
            const abs = Math.abs(offset);
            const scale = offset === 0 ? 1.08 : Math.max(0.76, 0.94 - abs * 0.06);
            const tilt = offset * -9;
            const provider = providerOf(game);
            return (
              <button
                key={game.game_id}
                type="button"
                className={`slot-show-card${offset === 0 ? " is-center" : ""}`}
                style={{
                  transform: `translateX(calc(-50% + ${offset * 158}px)) scale(${scale}) rotateY(${tilt}deg)`,
                  zIndex: 20 - abs,
                  opacity: abs > 4 ? 0 : abs > 3 ? 0.28 : 1,
                  pointerEvents: abs > 4 ? "none" : "auto",
                }}
                onClick={() => openIfCenter(game, offset)}
              >
                <div
                  className={`slot-show-art${broken[game.game_id] ? " is-fallback" : ""}`}
                  style={broken[game.game_id] ? { background: `linear-gradient(165deg, hsl(${hueOf(game.game_name)} 70% 52%), #120d22)` } : undefined}
                >
                  {broken[game.game_id] ? null : (
                    <img
                      src={resolveGameCover(game, 1100)}
                      alt=""
                      width={256}
                      height={359}
                      loading={offset === 0 ? "eager" : "lazy"}
                      decoding="async"
                      referrerPolicy="no-referrer"
                      draggable={false}
                      onError={() => setBroken((prev) => ({ ...prev, [game.game_id]: true }))}
                    />
                  )}
                </div>
                <div className="slot-show-glass">
                  <strong>{game.game_name}</strong>
                  {provider ? <small>{provider}</small> : null}
                  {offset === 0 ? <span className="slot-show-play">{playLabel}</span> : null}
                </div>
              </button>
            );
          })}
        </div>
        <button type="button" className="slot-show-nav" onClick={() => move(1)} aria-label="Sağa kaydır">
          <ChevronRightIcon />
        </button>
      </div>
      {items.length > 1 ? (
        <div className="slot-show-dots" role="tablist" aria-label={title}>
          {items.map((game, index) => (
            <button
              key={`dot-${game.game_id}`}
              type="button"
              role="tab"
              aria-selected={index === active}
              className={`slot-show-dot${index === active ? " is-on" : ""}`}
              onClick={() => setActive(index)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default SlotShowcase;
